// src/cockpit4/components/TodayOpportunityStrip.tsx
// ============================================
// P0-1 今日赚钱机会条带（Opportunity Radar）
//
// 数据流（缓存优先，不烧 Token）：
//   1. 挂载 → RuntimeManager.query(Autopilot, { type: 'get_latest_report' })
//      （Autopilot 每日 run 的报告内存缓存，零 LLM 调用）
//   2. 空态 → 用户点「扫描机会」→ execute(Autopilot, 'run') 全量扫描
//      （LLM 可用则分析，不可用自动降级规则化；报告落 Vault）
//   3. 每次 run 的 opportunities 自动写盘（Autopilot-Report-{date}.md）
//
// 展示：Top3 机会 + Opportunity Score + 预计收入 + 第一动作
// ============================================

import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Flame, Loader2, RefreshCw, Users, Target, Zap, ArrowRight,
} from "lucide-react";
import RuntimeManager from "../../runtime/RuntimeManager";
import { RuntimeName } from "../../types/runtime";
import {
  AutopilotQuery,
  AutopilotCommand,
  type PrioritizedOpportunity,
} from "../../runtime/Autopilot/types";

/** 今日日期 YYYY-MM-DD */
function today(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** 金额格式化（千分位） */
function fmt(v: number): string {
  if (v >= 10000) return `${(v / 10000).toFixed(1)} 万`;
  return v.toLocaleString("zh-CN");
}

/** 投入强度文案 */
const EFFORT_TEXT: Record<string, string> = {
  low: "低投入",
  medium: "中投入",
  high: "高投入",
};

export default function TodayOpportunityStrip({ date }: { date?: string }) {
  const [opportunities, setOpportunities] = useState<PrioritizedOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [msg, setMsg] = useState<{ type: "ok" | "err"; text: string } | null>(null);
  const [scannedAt, setScannedAt] = useState<string>("");

  // 缓存优先：读取最新 Autopilot 报告中的机会（不触发 LLM）
  const loadCached = useCallback(async () => {
    setLoading(true);
    try {
      if (!RuntimeManager.isInitialized() || !RuntimeManager.hasRuntime(RuntimeName.Autopilot)) {
        setOpportunities([]);
        setMsg({ type: "err", text: "Autopilot 引擎未就绪，请稍后重试或点击扫描" });
        return;
      }
      const resp = await RuntimeManager.query(RuntimeName.Autopilot, {
        type: AutopilotQuery.GetLatestReport,
      } as any);
      const data = resp?.data as any;
      const list: PrioritizedOpportunity[] = Array.isArray(data?.opportunities)
        ? data.opportunities
        : [];
      setOpportunities(list);
      setScannedAt(data?.date ? String(data.date) : "");
    } catch {
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadCached();
  }, [loadCached]);

  // 全量扫描：触发 Autopilot run（LLM 可用则分析，否则规则化；自动落 Vault）
  const scan = useCallback(async () => {
    if (scanning) return;
    setScanning(true);
    setMsg(null);
    try {
      const resp = await RuntimeManager.execute(
        RuntimeName.Autopilot,
        AutopilotCommand.Run,
        { date: date || today() }
      );
      const data = resp?.data as any;
      const list: PrioritizedOpportunity[] = Array.isArray(data?.opportunities)
        ? data.opportunities
        : [];
      setOpportunities(list);
      setScannedAt(data?.date ? String(data.date) : today());
      if (list.length > 0) {
        setMsg({
          type: "ok",
          text: `已扫描 ${list.length} 个机会${data?.usedLLM ? "（AI 分析）" : "（规则化）"}${
            data?.reportPath ? "，已落盘" : ""
          }`,
        });
      } else {
        setMsg({ type: "err", text: "暂未发现可排序的机会，稍后再试" });
      }
    } catch (e: any) {
      setMsg({ type: "err", text: e?.message || "扫描失败" });
    } finally {
      setScanning(false);
    }
  }, [date, scanning]);

  const top3 = opportunities.slice(0, 3);
  const scoreColor = (s: number) => (s >= 80 ? "#34D399" : s >= 60 ? "#FBBF24" : "#F87171");

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      className="relative rounded-2xl border border-white/[0.06] bg-white/[0.02] backdrop-blur-xl p-4"
    >
      {/* 顶部装饰光带 */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-500/50 to-transparent" />

      {/* 标题行 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-6 h-6 rounded-md flex items-center justify-center bg-amber-500/15 border border-amber-500/30">
            <Flame size={13} className="text-amber-400" />
          </span>
          <div>
            <span className="text-[12px] font-semibold text-white tracking-tight">
              今日赚钱机会
            </span>
            <span className="ml-2 text-[9px] font-mono text-gray-500 uppercase tracking-wider">
              Opportunity Radar · Autopilot
            </span>
          </div>
          {scannedAt && (
            <span className="text-[9px] font-mono text-gray-500">({scannedAt})</span>
          )}
        </div>
        <button
          onClick={() => void scan()}
          disabled={scanning || loading}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-amber-500/15 border border-amber-500/30 hover:bg-amber-500/25 transition-colors disabled:opacity-50"
          title="基于内容/客户/产品/市场信号全量扫描机会（消耗 Token）"
        >
          {scanning ? (
            <Loader2 size={11} className="text-amber-400 animate-spin" />
          ) : (
            <RefreshCw size={11} className="text-amber-400" />
          )}
          <span className="text-[10px] font-mono text-amber-300 uppercase tracking-wider">
            {scanning ? "扫描中..." : "扫描机会"}
          </span>
        </button>
      </div>

      {/* 提示信息 */}
      {msg && (
        <div
          className={`mb-3 px-3 py-1.5 rounded-md border text-[10px] font-mono ${
            msg.type === "ok"
              ? "border-emerald-500/30 bg-emerald-500/[0.06] text-emerald-300"
              : "border-amber-500/30 bg-amber-500/[0.06] text-amber-300"
          }`}
        >
          {msg.text}
        </div>
      )}

      {/* 内容：加载骨架 / 空态 / Top3 卡片 */}
      {loading ? (
        <div className="grid grid-cols-3 gap-2.5">
          {[0, 1, 2].map((i) => (
            <div key={i} className="rounded-xl border border-white/[0.04] bg-white/[0.01] p-3 animate-pulse">
              <div className="h-3 w-2/3 bg-white/10 rounded mb-2" />
              <div className="h-2 w-full bg-white/5 rounded mb-1.5" />
              <div className="h-2 w-1/2 bg-white/5 rounded" />
            </div>
          ))}
        </div>
      ) : top3.length === 0 ? (
        <div className="rounded-xl border border-dashed border-white/[0.08] px-4 py-6 text-center">
          <div className="text-[11px] font-mono text-gray-500">
            暂无机会数据 — 点击「扫描机会」让 Autopilot 基于内容 / 客户 / 产品 / 市场信号排序当前最可能赚钱的方向
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
          {top3.map((op, idx) => (
            <motion.div
              key={op.id || op.title}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, delay: idx * 0.06 }}
              className="relative rounded-xl border border-white/[0.06] bg-white/[0.02] p-3 hover:bg-white/[0.04] transition-colors overflow-hidden"
            >
              {/* 排名角标 */}
              <span className="absolute top-2 right-2 text-[10px] font-bold text-amber-400/80">
                #{idx + 1}
              </span>

              <div className="flex items-center gap-1.5 mb-1.5">
                <span className="text-[11px] font-medium text-white truncate">{op.title}</span>
              </div>

              {/* Opportunity Score */}
              <div className="flex items-center gap-2 mb-2">
                <div className="flex-1 h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${op.score}%`, background: scoreColor(op.score) }}
                  />
                </div>
                <span className="text-[11px] font-mono font-bold" style={{ color: scoreColor(op.score) }}>
                  {op.score} 分
                </span>
              </div>

              {/* 关键指标 */}
              <div className="grid grid-cols-2 gap-x-2 gap-y-1 mb-2 text-[9px] font-mono">
                <div className="flex items-center gap-1 text-gray-400">
                  <Target size={9} className="text-amber-400" />
                  预计收入 <span className="text-amber-200 font-semibold">¥{fmt(op.potentialRevenue)}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Zap size={9} className="text-emerald-400" />
                  成交概率 <span className="text-emerald-300 font-semibold">{Math.round((op.probability || 0) * 100)}%</span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Users size={9} className="text-sky-400" />
                  投入 <span className="text-gray-200">{EFFORT_TEXT[op.effort] || op.effort}</span>
                </div>
                <div className="flex items-center gap-1 text-gray-400">
                  <Flame size={9} className="text-rose-400" />
                  周期 <span className="text-gray-200">{op.timeframe || "-"}</span>
                </div>
              </div>

              {/* 第一动作 */}
              {op.recommendation && (
                <div className="flex items-start gap-1.5 rounded-md bg-white/[0.03] border border-white/[0.05] px-2 py-1.5">
                  <ArrowRight size={9} className="text-amber-400 mt-0.5 flex-shrink-0" />
                  <span className="text-[9px] text-gray-300 leading-snug line-clamp-2">
                    {op.recommendation}
                  </span>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      )}
    </motion.section>
  );
}
